import { array, option, record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { useMemo } from 'react';
import { isEmptyRecord } from '@mews/fp';
import { ReservationsTableColumns, hideableColumns, loyaltyColumns, useLoyaltyProgramsGetAllQuery } from '../../common';
import { additionalColumnDependencies } from './useColumns';

const concatDependentColumnIds = (columnNames: Array<ReservationsTableColumns>) => {
    const additionalColumnNames = pipe(
        columnNames,
        array.map((value) => option.fromNullable(additionalColumnDependencies[value])),
        array.compact,
        array.flatten
    );

    return [...columnNames, ...additionalColumnNames];
};

const createDefaultColumnsState = (columns: Array<{ value: ReservationsTableColumns }>) =>
    pipe(
        columns,
        array.map((column) => column.value),
        concatDependentColumnIds,
        array.map<string, [string, boolean]>((value) => [value, false]),
        record.fromEntries
    );

const defaultHideableColumnsState = createDefaultColumnsState(hideableColumns);
const loyaltyHideableColumnsState = createDefaultColumnsState(loyaltyColumns);

interface UseColumnsVisibilityParams {
    visibleColumns?: Array<ReservationsTableColumns>;
}

export const useColumnsVisibility = ({ visibleColumns }: UseColumnsVisibilityParams) => {
    const { programs } = useLoyaltyProgramsGetAllQuery();
    const hasLoyaltyPrograms = !isEmptyRecord(programs);

    return useMemo(() => {
        if (!visibleColumns || array.isEmpty(visibleColumns)) {
            return hasLoyaltyPrograms ? undefined : loyaltyHideableColumnsState;
        }

        const columnNames = concatDependentColumnIds(visibleColumns);

        return record.filterWithIndex((key) => !columnNames.includes(key))(defaultHideableColumnsState);
    }, [hasLoyaltyPrograms, visibleColumns]);
};
